// Public help-center progressive enhancement (BC-93). Plain DOM, no framework —
// the page is server-rendered ERB and fully usable without JS. Kept in a Vite
// entrypoint (not inline) so it stays CSP-friendly.

import confetti from 'canvas-confetti'
import 'htmx.org' // server-driven typeahead: input fires hx-get, htmx swaps results

function postBeacon(url: string, body?: Record<string, unknown>): void {
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
    keepalive: true,
  }).catch(() => {
    /* best-effort; counts are approximate */
  })
}

// Celebratory confetti burst from an element, in the account's brand colour.
function celebrate(origin: HTMLElement): void {
  const rect = origin.getBoundingClientRect()
  const primary =
    getComputedStyle(document.documentElement).getPropertyValue('--hc-primary').trim() || '#10b981'

  confetti({
    particleCount: 70,
    spread: 70,
    startVelocity: 34,
    scalar: 0.9,
    ticks: 140,
    origin: {
      x: (rect.left + rect.width / 2) / window.innerWidth,
      y: (rect.top + rect.height / 2) / window.innerHeight,
    },
    colors: [primary, '#ffffff', '#fbbf24'],
    disableForReducedMotion: true,
  })
}

// Article feedback (helpful / not-helpful) with a delightful confirm animation.
function initFeedback(): void {
  const widget = document.querySelector<HTMLElement>('.hc-feedback')
  if (!widget) return
  const url = widget.dataset.feedbackUrl
  if (!url) return

  const buttons = Array.from(widget.querySelectorAll<HTMLButtonElement>('[data-verdict]'))
  const thanksText = widget.querySelector<HTMLElement>('[data-thanks-text]')

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      if (widget.getAttribute('data-done') === 'true') return
      const verdict = button.dataset.verdict

      buttons.forEach((other) => other.classList.toggle('is-dimmed', other !== button))
      button.classList.add('is-selected')

      if (verdict === 'helpful') {
        celebrate(button)
      } else if (thanksText) {
        thanksText.textContent = 'Thanks — we’ll make this better.'
      }

      window.setTimeout(() => widget.setAttribute('data-done', 'true'), 260)
      postBeacon(url, { verdict })
    })
  })
}

// Fire the view beacon once per load (server dedupes per cookie/window).
function initViewBeacon(): void {
  const article = document.querySelector<HTMLElement>('.hc-article')
  const url = article?.dataset.viewUrl
  if (url) postBeacon(url)
}

// TOC scrollspy — highlight the heading currently in view.
function initScrollspy(): void {
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('[data-toc-link]'))
  if (links.length === 0) return

  const byId = new Map<string, HTMLAnchorElement>()
  links.forEach((link) => {
    const id = link.getAttribute('href')?.slice(1)
    if (id) byId.set(id, link)
  })

  const headings = Array.from(byId.keys())
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => el !== null)

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        links.forEach((link) => link.classList.remove('is-active'))
        byId.get(entry.target.id)?.classList.add('is-active')
      })
    },
    { rootMargin: '-10% 0px -75% 0px', threshold: 0 }
  )

  headings.forEach((heading) => observer.observe(heading))
}

// Visitor dark/light toggle. Defaults to the account's configured appearance
// (server-rendered class); an explicit choice is persisted in localStorage and
// re-applied before paint by the inline script in the layout head.
const THEME_KEY = 'hc-theme'

function resolvedMode(): 'dark' | 'light' {
  const root = document.documentElement
  if (root.classList.contains('hc-light')) return 'light'
  if (root.classList.contains('hc-dark')) return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

function applyMode(mode: 'dark' | 'light'): void {
  const root = document.documentElement
  root.classList.remove('hc-dark', 'hc-light', 'hc-system')
  root.classList.add(`hc-${mode}`)
  root.setAttribute('data-hc-mode', mode)
}

function initThemeToggle(): void {
  const toggle = document.querySelector<HTMLButtonElement>('[data-theme-toggle]')
  if (!toggle) return

  // Reflect the current (possibly system-resolved) mode so the right icon shows.
  document.documentElement.setAttribute('data-hc-mode', resolvedMode())

  toggle.addEventListener('click', () => {
    const next = resolvedMode() === 'dark' ? 'light' : 'dark'
    applyMode(next)
    try {
      localStorage.setItem(THEME_KEY, next)
    } catch {
      /* private mode / storage disabled — toggle still works for this page */
    }
  })
}

// Command palette. htmx fetches/swaps the results; this drives open/close
// (⌘K / Ctrl-K / "/", any [data-palette-open] trigger, backdrop, Esc) and
// arrow-key navigation over the results.
function initPalette(): void {
  const palette = document.querySelector<HTMLElement>('[data-palette]')
  const input = palette?.querySelector<HTMLInputElement>('[data-palette-input]')
  const results = palette?.querySelector<HTMLElement>('.hc-palette__results')
  if (!palette || !input || !results) return
  let active = -1

  const items = () => Array.from(results.querySelectorAll<HTMLAnchorElement>('.hc-suggest__item'))
  const highlight = (next: number) => {
    const list = items()
    if (list.length === 0) return
    active = (next + list.length) % list.length
    list.forEach((el, i) => el.classList.toggle('is-active', i === active))
    list[active]?.scrollIntoView({ block: 'nearest' })
  }
  const isOpen = () => !palette.hasAttribute('hidden')
  const open = () => {
    if (isOpen()) return
    palette.removeAttribute('hidden')
    document.body.style.overflow = 'hidden'
    input.focus()
    input.select()
  }
  const close = () => {
    palette.setAttribute('hidden', '')
    document.body.style.overflow = ''
    results.innerHTML = ''
    active = -1
  }

  document.querySelectorAll<HTMLElement>('[data-palette-open]').forEach((trigger) =>
    trigger.addEventListener('click', open)
  )
  palette.querySelectorAll<HTMLElement>('[data-palette-close]').forEach((el) =>
    el.addEventListener('click', close)
  )

  document.addEventListener('keydown', (event) => {
    const key = event.key.toLowerCase()

    if ((event.metaKey || event.ctrlKey) && key === 'k') {
      event.preventDefault()
      if (isOpen()) close()
      else open()
      return
    }
    if (key === '/' && !isOpen()) {
      const tag = (event.target as HTMLElement)?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return
      event.preventDefault()
      open()
      return
    }
    if (!isOpen()) return

    if (event.key === 'Escape') {
      event.preventDefault()
      close()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      highlight(active + 1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      highlight(active - 1)
    } else if (event.key === 'Enter') {
      const list = items()
      if (active >= 0 && list[active]) {
        event.preventDefault()
        window.location.href = list[active].href
      }
      // otherwise the form submits → full search results page
    }
  })

  // Reset selection + aria-expanded whenever htmx swaps in new results.
  new MutationObserver(() => {
    active = -1
    input.setAttribute('aria-expanded', results.children.length > 0 ? 'true' : 'false')
  }).observe(results, { childList: true })
}

// Atlas docs sidebar: remember which category groups the visitor collapses/
// expands (per-group, in localStorage). The server renders a sensible default
// (first group + the current page's group open, the rest collapsed); a stored
// preference overrides that, except the group holding the current page always
// stays open so you can see where you are.
const ATLAS_GROUPS_KEY = 'hc-atlas-groups'

function initAtlasSidebar(): void {
  // Mobile: the category list sits behind a toggle (hidden by CSS until opened).
  const nav = document.querySelector<HTMLElement>('[data-atlas-nav]')
  const toggle = nav?.querySelector<HTMLButtonElement>('[data-atlas-nav-toggle]')
  toggle?.addEventListener('click', () => {
    const open = nav!.classList.toggle('is-open')
    toggle.setAttribute('aria-expanded', String(open))
  })

  const groups = Array.from(
    document.querySelectorAll<HTMLDetailsElement>('.hc-atlas__group[data-hc-group]')
  )
  if (groups.length === 0) return

  let stored: Record<string, boolean> = {}
  try {
    stored = JSON.parse(localStorage.getItem(ATLAS_GROUPS_KEY) || '{}')
  } catch {
    /* storage disabled — fall back to server defaults */
  }

  groups.forEach((group) => {
    const id = group.dataset.hcGroup
    if (!id) return
    const isCurrent = group.querySelector('.is-current, .hc-atlas__links a.is-active') !== null

    if (!isCurrent && id in stored) group.open = stored[id]

    group.addEventListener('toggle', () => {
      try {
        stored[id] = group.open
        localStorage.setItem(ATLAS_GROUPS_KEY, JSON.stringify(stored))
      } catch {
        /* storage disabled — collapse still works for this page */
      }
    })
  })
}

// "Contact us" buttons open the embedded chat widget (if present).
function initChat(): void {
  document.querySelectorAll<HTMLElement>('[data-chat-open]').forEach((el) =>
    el.addEventListener('click', (event) => {
      event.preventDefault()
      ;(window as unknown as { banshee?: (command: string) => void }).banshee?.('open')
    })
  )
}

// Dismissing an announcement banner (BC-142). The page HTML is cached and
// shared between visitors, so the dismissal can only live in the browser: the
// key carries the banner's updated_at, so editing a banner re-shows it to
// people who dismissed the previous wording. Hiding a banner already dismissed
// happens in an inline script in the partial, before first paint.
function initBanners(): void {
  document.querySelectorAll<HTMLElement>('[data-banner-dismiss]').forEach((button) =>
    button.addEventListener('click', () => {
      const banner = button.closest<HTMLElement>('[data-banner-key]')
      if (!banner) return
      try {
        localStorage.setItem(banner.dataset.bannerKey ?? '', '1')
      } catch {
        // Private mode / blocked storage: still dismiss for this page view.
      }
      banner.remove()
    })
  )
}

function init(): void {
  initFeedback()
  initViewBeacon()
  initScrollspy()
  initThemeToggle()
  initPalette()
  initChat()
  initAtlasSidebar()
  initBanners()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
